<template>
  <div class="relative">
    <base-input
      :inset="inset"
      with-label
      :label="label || officeLabel"
      type="text"
      :id="`kode_${officeType}`"
      :placeholder="placeholder || officeTypePlaceholder"
      :disabled="disabled"
      :required="required"
      fullwidth
      :shadow="false"
      v-model="office.searchCode"
      @change="onSearchOffice"
    />
    <button
      v-if="withModal && !disabled"
      type="button"
      :class="['absolute cursor-pointer text-gray-400', inset ? 'right-4 top-6' : '']"
      :style="inset ? {} : { top: '2.4rem', right: '0.5rem' }"
      @click="onOpenOfficeModal"
    >
      <Icon class="h-5 w-5" icon="heroicons:magnifying-glass-circle-20-solid" />
    </button>

    <office-table-modal
      v-if="withModal"
      :visible="visibleOfficeModal"
      :office-type="officeType"
      :columns="tableColumns"
      :request-params="filterActive ? { 'filter[is_active]': true } : {}"
      :type="type"
      @close="onCloseOfficeModal"
      @click-office="onChangeOffice"
    />

    <teleport to="body">
      <loading v-if="loading" />
    </teleport>
  </div>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import OfficeTableModal from './office-table-modal.vue';
import Teleport from 'vue2-teleport';
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';

export default {
  components: { OfficeTableModal, Teleport },
  mixins: [requestMixin],
  props: {
    value: Object,
    withArea: Boolean,
    withCurrentWarehouse: Boolean,
    withStockistCenterBalance: Boolean,
    withOrderBalance: Boolean,
    withAddress: Boolean,
    withUpline: Boolean,
    withUplineHistories: Boolean,
    withPhone: Boolean,
    withCheckOrderDestination: Boolean,
    withCheckOrderOrigin: Boolean,
    checkOriginOfficeId: String,
    checkDestinationOfficeId: String,
    withTempCode: Boolean,
    useCustomAddress: Boolean,
    useBackupUplineAddress: Boolean,
    disabled: Boolean,
    required: Boolean,
    label: String,
    placeholder: String,
    tableColumns: {
      type: Array,
      default: () => ['address', 'phone']
    },
    officeType: {
      type: String,
      default: 'stockist_center',
    },
    withModal: {
      type: Boolean,
      default: true,
    },
    mode: {
      type: String,
      default: 'search',
    },
    filterActive: Boolean,
    inset: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'office'
    }
  },
  emits: ['input', 'change', 'search', 'open-modal', 'close-modal'],
  data() {
    return {
      loading: false,
      visibleOfficeModal: false,
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser'
    }),
    office: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    officeLabel() {
      return {
        stockist_center: 'Kode Stockist Center',
        supplier: 'Kode Supplier',
        pusat: 'Kode Pusat',
        kantor_pelayanan: 'Kode Kantor Pelayanan',
        stockist: 'Kode Stockist',
        member: 'Nomor ID Mitra Usaha',
      }[this.officeType];
    },
    officeTypePlaceholder() {
      return {
        stockist_center: 'SC1',
        supplier: 'HMI',
        pusat: 'PUSAT',
        kantor_pelayanan: 'KP1',
        stockist: 'AB.001',
        member: 'N-1',
      }[this.officeType];
    },
    officeCategoryId() {
      return {
        stockist_center: 3,
        supplier: 6,
        pusat: 1,
        kantor_pelayanan: 2,
        stockist: 4,
        member: 5,
      }[this.officeType];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async checkOrderDestination(destinationId) {
      const [res, err] = await this.request(`/api/v1/offices/${destinationId}/origin-order-destinations`, {
        params: {
          'filter[origin_office_id]': this.checkOriginOfficeId
        }
      })

      if (err) {
        return false
      }

      if (!res.meta.has_origin) {
        return true
      }

      return res.data.length > 0
    },
    async checkOrderOrigin(originId) {
      const [res, err] = await this.request(`/api/v1/offices/${this.checkDestinationOfficeId}/origin-order-destinations`, {
        params: {
          'filter[origin_office_id]': originId
        }
      })

      if (err) {
        return false
      }

      if (!res.meta.has_origin) {
        return true
      }

      return res.data.length > 0
    },
    onOpenOfficeModal() {
      this.visibleOfficeModal = true;

      this.$emit('open-modal')
    },
    onCloseOfficeModal() {
      this.visibleOfficeModal = false

      this.$emit('close-modal')
    },
    async onChangeOffice(office) {
      this.office.searchCode = office.attributes.code;

      await this.onSearchOffice();

      this.visibleOfficeModal = false;
    },
    async onSearchOffice() {
      this.loading = true;

      const officeFields = [
        ...(this.withStockistCenterBalance
          ? [
              'restock_balance',
              'multiplied_deposit_balance',
              'temp_restock_balance',
              'overpayment_balance',
              'deposit_balance',
            ]
          : []),
        ...(this.withOrderBalance ? ['order_balance_available'] : []),
        ...(this.withCurrentWarehouse ? ['current-warehouse'] : []),
        ...(this.withAddress ? ['full_address,default-address'] : []),
        ...(this.withArea ? ['area'] : []),
        ...(this.withUpline ? ['referred_by', 'referred_by_name'] : []),
        ...(this.withUplineHistories ? ['upline_histories'] : []),
        ...(this.useCustomAddress ? ['addresses'] : []),
      ].join(',');
      const simpleOfficeFields = [
        ...new Set([
          ...(this.withCurrentWarehouse ? ['office'] : []),
          ...(this.withArea ? ['office'] : []),
          ...(this.withStockistCenterBalance ? ['office'] : []),
          ...(this.withAddress ? ['office'] : []),
          'code',
          'name',
          'office_id',
          ...(this.withPhone ? ['cellphone'] : []),
          ...(this.withTempCode ? ['temp_code'] : []),
        ]),
      ].join(',');
      const include = [
        ...(this.withStockistCenterBalance ? ['office'] : []),
        ...(this.withCurrentWarehouse ? ['office.current-warehouse'] : []),
        ...(this.withArea ? ['office.area'] : []),
        ...(this.useCustomAddress ? ['office.addresses'] : []),
        ...(this.withAddress ? ['office.default-address'] : []),
      ].join(',');

      const [res, err] = await this.request(this.type === 'downline' ? `/api/v1/offices/${this.me.office_id}/simple-office-downlines` : `/api/v1/offices`, {
        params: {
          ...(this.filterActive ? { 'filter[is_active]': true } : {}),
          'filter[office_category_id]': this.officeCategoryId,
          'filter[code]': this.office.searchCode.split(' ')[0],
          ...(this.withCurrentWarehouse
            ? {
                'fields[warehouses]': 'code,name',
              }
            : {}),
          ...(this.withArea
            ? {
                'fields[areas]': 'code',
              }
            : {}),
          'fields[offices]': officeFields,
          'fields[simple-offices]': simpleOfficeFields,
          include: include,
        },
      });

      if (err || !res.data.length) {
        if (this.mode === 'search') {
          this.office.searchCode = this.office.originalCode;

          this.createAlert({
            data: 'Kode tidak ditemukan',
            status: 'error',
          });
        } else if (this.mode === 'input') {
          this.office.id = null;
          this.office.name = null;
          this.office.code = this.office.searchCode;
          this.office.originalCode = this.office.searchCode;
        }
      } else {
        const simpleOffice = res.data[0];

        if (this.withCheckOrderDestination && !await this.checkOrderDestination(simpleOffice.attributes.office_id)) {
          this.office.searchCode = this.office.originalCode;

          this.createAlert({
            data: `Kebijakan tidak bisa order ke stockist ${simpleOffice.attributes.code}`,
            status: 'error',
          });
        } else if (this.withCheckOrderOrigin && !await this.checkOrderOrigin(simpleOffice.attributes.office_id)) {
          this.office.searchCode = this.office.originalCode;

          this.createAlert({
            data: `Kebijakan tidak bisa order ke ${simpleOffice.attributes.code}`,
            status: 'error',
          });
        } else {
          this.office.id = simpleOffice.attributes.office_id;
          this.office.code = simpleOffice.attributes.code;
          this.office.searchCode = simpleOffice.attributes.code;
          this.office.originalCode = simpleOffice.attributes.code;
          this.office.name = simpleOffice.attributes.name;

          if (this.withPhone) {
            this.office.phone = simpleOffice.attributes.cellphone;
          }

          if (this.withTempCode) {
            this.office.tempCode = simpleOffice.attributes.temp_code;
          }

          if (this.withAddress) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );
            const defaultAddress = !office.relationships['default-address'].data ? null : this.getSingleIncluded(res, office.relationships['default-address'].data.id)

            if (!defaultAddress) {
              this.office.address = office.attributes.full_address;

              this.office.hasAddress = false
            } else {
              this.office.address = `${defaultAddress.attributes.detail}, ${defaultAddress.attributes.province_name}, ${defaultAddress.attributes.city_name}, ${defaultAddress.attributes.district_name}, ${defaultAddress.attributes.village_name}`;

              this.office.hasAddress = true
            }
          }

          if (this.withCurrentWarehouse) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );
            const currentWarehouse = this.getSingleIncluded(
              res,
              office.relationships['current-warehouse'].data.id
            );

            this.office.currentWarehouse.id = currentWarehouse.id;
            this.office.currentWarehouse.code = currentWarehouse.attributes.code;
            this.office.currentWarehouse.name = currentWarehouse.attributes.name;
          }

          if (this.withArea) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            if (office.relationships['area'].data) {
              const area = this.getSingleIncluded(
                res,
                office.relationships['area'].data.id
              );

              this.office.area.id = area.id;
              this.office.area.code = area.attributes.code;
              this.office.searchCode = `${simpleOffice.attributes.code} (${area.attributes.code})`;
              this.office.originalCode = `${simpleOffice.attributes.code} (${area.attributes.code})`;
            }
          }

          if (this.withStockistCenterBalance) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            this.office.restock_balance = office.attributes.restock_balance;
            this.office.temp_restock_balance =
              office.attributes.temp_restock_balance;
            this.office.multiplied_deposit_balance =
              office.attributes.multiplied_deposit_balance;
            this.office.overpayment_balance =
              office.attributes.overpayment_balance;
            this.office.deposit_balance = office.attributes.deposit_balance;
          }

          if (this.withOrderBalance) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            this.office.order_balance = office.attributes.order_balance_available;
          }

          if (this.withUpline) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            this.office.uplineCode = office.attributes.referred_by;
            this.office.uplineName = office.attributes.referred_by_name;
          }

          if (this.withUplineHistories) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            this.office.uplineHistories = office.attributes.upline_histories;
          }

          if (this.useCustomAddress) {
            const office = this.getSingleIncluded(
              res,
              simpleOffice.relationships.office.data.id
            );

            const addresses = this.getIncludedByType(res, 'addresses', office);

            if (addresses.length) {
              this.office.hasAddress = true
            }

            if (addresses.length || !this.useBackupUplineAddress) {
              this.$emit('change', {
                addresses: this.getIncludedByType(res, 'addresses', office),
              });
            } else {
              const [uplineData, err] = await this.request(
                `/api/v1/offices/${office.id}/uplines`,
                {
                  params: {
                    'filter[office_type]': 'stockist',
                    include: 'addresses,default-address',
                    'fields[offices]': 'addresses,default-address',
                  },
                }
              );

              if (!err && uplineData.data.length > 0) {
                const [upline] = uplineData.data;

                if (upline.relationships['default-address'].data) {
                  this.$emit('change', {
                    address: this.getSingleIncluded(uplineData, upline.relationships['default-address'].data.id),
                  });

                  this.office.hasAddress = true
                } else {
                  this.$emit('change', {
                    addresses: this.getIncludedByType(
                      uplineData,
                      'addresses',
                      upline
                    ),
                  });
                }
              } else {
                this.$emit('change');    
              }
            }
          } else {
            this.$emit('change');
          } 
        }
      }

      this.$emit('search');

      this.loading = false;
    },
  },
};
</script>
